import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ImagePlus, Loader2, Mail, MailOpen } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NoteCard } from "@/components/NoteCard";
import { Petals } from "@/components/Petals";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [picture, setPicture] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (user === false) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setBio(user.bio || "");
    setContactEmail(user.contact_email || "");
    setIsPublic(user.privacy === "public");
    setEmailAlerts(user.email_alerts !== false);
    setPicture(user.picture || null);
    api.get(`/members/${user.id}`).then((r) => setData(r.data));
    api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
  }, [user]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPicture(data.path);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.put("/profile", {
        name, bio, contact_email: contactEmail, picture,
        privacy: isPublic ? "public" : "private",
        email_alerts: emailAlerts,
      });
      setUser(updated);
      toast.success("Profile updated 🌹");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!user) {
    return (
      <div className="garden-bg flex justify-center pt-32">
        <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="garden-bg">
      <Petals count={6} />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 pt-14 pb-10">
        <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 mb-10">
          <button onClick={() => fileRef.current?.click()} className="relative group" data-testid="profile-avatar-btn">
            <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-pink-500/40 bg-pink-500/10 flex items-center justify-center">
              {picture ? (
                <img src={fileUrl(picture)} alt="you" className="w-full h-full object-cover" />
              ) : (
                <span className="font-serif text-4xl text-pink-300">{name?.[0]?.toUpperCase() || "?"}</span>
              )}
            </div>
            <span className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              {uploading ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <ImagePlus className="w-6 h-6 text-white" />}
            </span>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} data-testid="profile-avatar-file" />
          </button>
          <div className="text-center sm:text-left">
            <h1 className="font-serif text-4xl font-bold text-pink-50 text-glow">{name || "Your profile"}</h1>
            <p className="text-slate-400">{user.email}</p>
          </div>
        </div>

        {/* Settings card */}
        <div className="card-glass rounded-2xl p-6 sm:p-8 border border-pink-500/20 space-y-5 mb-10">
          <h2 className="font-serif text-2xl text-pink-100">Your details</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-pink-200 text-sm">Display name</Label>
              <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="bg-black/40 border-pink-500/25 text-pink-50" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-pink-200 text-sm">Contact email (shown if public)</Label>
              <Input data-testid="profile-contact-input" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="reach-me@example.com" className="bg-black/40 border-pink-500/25 text-pink-50" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-pink-200 text-sm">Bio</Label>
            <Textarea data-testid="profile-bio-input" value={bio} onChange={(e) => setBio(e.target.value)} rows={2} placeholder="A line about you…" className="bg-black/40 border-pink-500/25 text-pink-50 resize-none" />
          </div>
          <div className="flex items-center justify-between bg-black/30 rounded-xl px-4 py-3 border border-pink-500/15">
            <div>
              <p className="text-sm text-pink-100">{isPublic ? "Share contact" : "Stay private"}</p>
              <p className="text-xs text-slate-400">
                {isPublic ? "Members can see your email and message you in-app." : "Your email stays hidden. In-app messages still reach you."}
              </p>
            </div>
            <Switch data-testid="profile-privacy-switch" className="data-[state=unchecked]:bg-slate-600 data-[state=checked]:bg-pink-500 [&>span]:bg-white" checked={isPublic} onCheckedChange={setIsPublic} />
          </div>
          <div className="flex items-center justify-between bg-black/30 rounded-xl px-4 py-3 border border-pink-500/15">
            <div>
              <p className="text-sm text-pink-100">Rose Alerts</p>
              <p className="text-xs text-slate-400">Email me the moment a note or rose lands for me.</p>
            </div>
            <Switch data-testid="profile-email-alerts-switch" className="data-[state=unchecked]:bg-slate-600 data-[state=checked]:bg-pink-500 [&>span]:bg-white" checked={emailAlerts} onCheckedChange={setEmailAlerts} />
          </div>
          <button
            data-testid="profile-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-500 hover:to-rose-400 text-white font-medium px-6 py-3 rounded-full transition-all hover:scale-[1.02] disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save profile"}
          </button>
        </div>

        {/* Messages inbox */}
        <div className="card-glass rounded-2xl p-6 sm:p-8 border border-pink-500/20 mb-10" data-testid="profile-inbox">
          <h2 className="font-serif text-2xl text-pink-100 mb-4 flex items-center gap-2"><Mail className="w-5 h-5 text-pink-400" /> Your messages</h2>
          {messages.length === 0 ? (
            <p className="text-slate-400 text-sm">No messages yet.</p>
          ) : (
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.id} className="bg-black/30 rounded-xl px-4 py-3 border border-pink-500/15">
                  <div className="flex items-center gap-2 mb-1">
                    <MailOpen className="w-4 h-4 text-pink-400" />
                    <span className="text-pink-200 text-sm font-medium">{m.from_name || "A member"}</span>
                    <span className="text-slate-500 text-xs ml-auto">{new Date(m.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-slate-300 text-sm">{m.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes tabs */}
        <Tabs defaultValue="received">
          <TabsList className="bg-black/40 border border-pink-500/20">
            <TabsTrigger value="received" data-testid="profile-tab-received" className="data-[state=active]:bg-pink-500/20 data-[state=active]:text-pink-200">
              Notes Received ({data?.notes_received?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="left" data-testid="profile-tab-left" className="data-[state=active]:bg-pink-500/20 data-[state=active]:text-pink-200">
              Notes Left ({data?.notes_left?.length || 0})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="received" className="mt-6">
            {data?.notes_received?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data.notes_received.map((n, i) => <NoteCard key={n.id} note={n} index={i} />)}
              </div>
            ) : <p className="text-slate-400 text-sm">No notes received yet. Notes addressed to your name will appear here.</p>}
          </TabsContent>
          <TabsContent value="left" className="mt-6">
            {data?.notes_left?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data.notes_left.map((n, i) => <NoteCard key={n.id} note={n} index={i} />)}
              </div>
            ) : <p className="text-slate-400 text-sm">You haven't left any notes yet.</p>}
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
